/**
 * Profile API Service
 * Handles profile, billing, credits, and session management API calls
 */

import { env } from '@/utils/env';
import { apiClient } from './client';
import type {
  CreditBalance,
  CreditHistoryResponse,
  UsageSummary,
  BillingProfile,
  UpdateBillingProfileRequest,
  InvoiceListResponse,
  InvoiceDetail,
  Session,
  CreditPricing,
  CreditPurchaseOrderResponse,
  CreditPurchaseListResponse,
  CreateCreditPurchaseRequest,
} from '@/types/profile';

/**
 * Credits API service
 */
export const creditsService = {
  async getBalance(): Promise<CreditBalance> {
    return apiClient.get<CreditBalance>('/api/Auth/credits');
  },

  async getHistory(limit = 20, offset = 0): Promise<CreditHistoryResponse> {
    return apiClient.get<CreditHistoryResponse>(
      `/api/Auth/credits/history?limit=${limit}&offset=${offset}`
    );
  },

  async getUsage(startDate?: string, endDate?: string): Promise<UsageSummary> {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const qs = params.toString();
    return apiClient.get<UsageSummary>(`/api/Billing/usage/my${qs ? `?${qs}` : ''}`);
  },
};

/**
 * Billing API service
 */
export const billingService = {
  async getProfile(): Promise<BillingProfile> {
    return apiClient.get<BillingProfile>('/api/Billing/profile');
  },

  async updateProfile(data: UpdateBillingProfileRequest): Promise<BillingProfile> {
    return apiClient.put<BillingProfile>('/api/Billing/profile', data);
  },

  async getInvoices(limit = 20, offset = 0): Promise<InvoiceListResponse> {
    return apiClient.get<InvoiceListResponse>(
      `/api/Billing/invoices/my?limit=${limit}&offset=${offset}`
    );
  },

  async getInvoiceDetail(id: string): Promise<InvoiceDetail> {
    return apiClient.get<InvoiceDetail>(`/api/Billing/invoices/${id}`);
  },

  async downloadInvoicePdf(id: string): Promise<Blob> {
    const url = `${env.apiUrl}/api/Billing/invoices/${id}/pdf`;
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Failed to download invoice PDF');
    return response.blob();
  },
};

/**
 * Credit Purchase API service
 */
export const creditPurchaseService = {
  async getPricing(): Promise<CreditPricing> {
    return apiClient.get<CreditPricing>('/api/credit-purchases/pricing');
  },

  async placeOrder(quantity: number): Promise<CreditPurchaseOrderResponse> {
    return apiClient.post<CreditPurchaseOrderResponse>('/api/credit-purchases', {
      quantity,
    } satisfies CreateCreditPurchaseRequest);
  },

  async getOrders(limit = 20, offset = 0): Promise<CreditPurchaseListResponse> {
    return apiClient.get<CreditPurchaseListResponse>(
      `/api/credit-purchases?limit=${limit}&offset=${offset}`
    );
  },

  async getOrder(id: string): Promise<CreditPurchaseOrderResponse> {
    return apiClient.get<CreditPurchaseOrderResponse>(`/api/credit-purchases/${id}`);
  },

  async cancelOrder(id: string, reason?: string): Promise<CreditPurchaseOrderResponse> {
    return apiClient.post<CreditPurchaseOrderResponse>(
      `/api/credit-purchases/${id}/cancel`,
      reason ? { reason } : {}
    );
  },
};

/**
 * Sessions API service
 */
export const sessionsService = {
  async getSessions(): Promise<Session[]> {
    return apiClient.get<Session[]>('/api/Auth/sessions');
  },

  async revokeSession(id: string): Promise<void> {
    return apiClient.delete<void>(`/api/Auth/sessions/${id}`);
  },

  async revokeAllOtherSessions(): Promise<void> {
    return apiClient.delete<void>('/api/Auth/sessions');
  },
};
